import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import DownloadIcon from '@mui/icons-material/Download';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { GridColDef } from '@mui/x-data-grid-premium';
import { reportService } from '../../api';
import { Table } from '../../components/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { fetchPortfolioSnapshotReport } from '../../redux/modules/report/report.actions';
import { reportSelector } from '../../redux/modules/report/report.selectors';
import type { PortfolioSnapshotReportApiItem, ReportApiDateRangeQuery } from '../../types/report';
import { gridFormatDecimalString, gridFormatDecimalString4 } from './reportGridFormatters';
import { ReportDateRangeFilter } from './ReportDateRangeFilter';
import { useReportCsvExport } from './useReportCsvExport';

function portfolioSnapshotRowId(row: PortfolioSnapshotReportApiItem) {
  return `${row.subscribed_portfolio_id}-${row.snapshot_date}`;
}

/** One row per subscribed portfolio per Riyadh day, written nightly. No dates selected = the full history. */
export function PortfolioSnapshotReportPage() {
  const [dateRange, setDateRange] = useState<ReportApiDateRangeQuery | null>(null);
  const onDateRangeChange = useCallback((r: ReportApiDateRangeQuery | null) => {
    setDateRange(r);
  }, []);

  const columns = useRef<GridColDef[]>([
    { field: 'snapshot_date', headerName: 'Date', width: 120 },
    { field: 'user_id', headerName: 'User Id', type: 'number', width: 100 },
    { field: 'subscribed_portfolio_id', headerName: 'Subscription Id', type: 'number', width: 130 },
    { field: 'template_name', headerName: 'Portfolio', minWidth: 180, flex: 0.3, sortable: false },
    { field: 'portfolio_status', headerName: 'Status', width: 130 },
    { field: 'is_closed', headerName: 'Closed', width: 90, sortable: false },
    { field: 'market_value', headerName: 'Market value', width: 160, valueFormatter: gridFormatDecimalString },
    {
      field: 'portfolio_cost_base',
      headerName: 'Cost base',
      width: 160,
      valueFormatter: gridFormatDecimalString,
    },
    {
      field: 'total_units',
      headerName: 'Total units',
      width: 140,
      sortable: false,
      valueFormatter: gridFormatDecimalString4,
    },
    {
      field: 'cash_allocation',
      headerName: 'Cash allocation',
      width: 150,
      sortable: false,
      valueFormatter: gridFormatDecimalString,
    },
    {
      field: 'invested_amount',
      headerName: 'Invested amount',
      width: 150,
      sortable: false,
      valueFormatter: gridFormatDecimalString,
    },
  ]);

  const { portfolioSnapshotReportItems, totalPortfolioSnapshotReportItems, fetchPortfolioSnapshotReportStatus, error } =
    useSelector(reportSelector);

  const { exportCsv, loading: exportCsvLoading } = useReportCsvExport(
    'portfolio-snapshot-report.csv',
    useCallback(() => reportService.exportPortfolioSnapshotReportCsv(dateRange ?? {}), [dateRange]),
  );

  return (
    <>
      {error && <Alert severity="error">{error.message}</Alert>}
      <Grid container flexDirection="column" component={Paper} sx={{ p: 3, mt: 0 }}>
        <ReportDateRangeFilter onRangeChange={onDateRangeChange} />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="h6">Portfolio snapshot</Typography>
          <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
            <Typography variant="caption" color="text.secondary">
              Riyadh days, up to yesterday. {dateRange ? 'Exports the selected dates.' : 'Exports the full history.'}
            </Typography>
            <LoadingButton
              type="button"
              variant="outlined"
              color="primary"
              endIcon={<DownloadIcon />}
              loading={exportCsvLoading}
              loadingPosition="end"
              onClick={exportCsv}
            >
              Export CSV
            </LoadingButton>
          </Stack>
        </Stack>
        <Table
          wrapperHeight={700}
          fetchItems={fetchPortfolioSnapshotReport}
          fetchItemsCustomParams={dateRange ?? {}}
          rows={portfolioSnapshotReportItems}
          columns={columns.current}
          getRowId={portfolioSnapshotRowId}
          loading={fetchPortfolioSnapshotReportStatus === fetchStatuses.pending}
          rowCount={totalPortfolioSnapshotReportItems}
          initialSortModel={[{ field: 'snapshot_date', sort: 'desc' }]}
        />
      </Grid>
    </>
  );
}
